/** Global styles injected into document.head — ONLY what must affect the host page */
export const GLOBAL_CSS = /* css */ `
body.ik-annotating,
body.ik-annotating * { cursor: crosshair !important; }
`

/** Toolbar shadow DOM styles — fully isolated */
export const TOOLBAR_CSS = /* css */ `
:host {
  all: initial;
  display: block;
  position: fixed;
  z-index: 2147483646;
}

* { box-sizing: border-box; }

:host-context([data-visual-qa-theme="dark"]),
@media (prefers-color-scheme: dark) {
  :host {
    --ik-bg: #1c1c1e; --ik-bg2: #2c2c2e; --ik-border: #38383a;
    --ik-text: #f4f4f5; --ik-muted: #a1a1aa;
    --ik-shadow: 0 8px 32px rgba(0,0,0,.4), 0 0 0 1px rgba(255,255,255,.06);
  }
}

:host {
  --ik-accent: #5b5bd6;
  --ik-accent-h: #4747be;
  --ik-bg: #ffffff;
  --ik-bg2: #f7f7f8;
  --ik-border: #e2e2e5;
  --ik-text: #18181b;
  --ik-muted: #a1a1aa;
  --ik-shadow: 0 8px 32px rgba(0,0,0,.08), 0 0 0 1px rgba(0,0,0,.04);
}

.toolbar {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  background: var(--ik-bg);
  border-radius: 10px;
  padding: 6px;
  box-shadow: var(--ik-shadow);
  user-select: none;
  touch-action: none;
  cursor: grab;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}
.toolbar:active { cursor: grabbing; }

.drag-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 10px;
  cursor: grab;
  opacity: 0.35;
  transition: opacity 0.15s ease;
  flex-shrink: 0;
  margin-bottom: 2px;
}
.drag-handle:hover { opacity: 0.7; }
.drag-handle:active { cursor: grabbing; opacity: 0.9; }
.drag-handle svg { pointer-events: none; }

.btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: var(--ik-muted);
  cursor: pointer;
  padding: 0;
  position: relative;
  transition: background .15s ease, color .15s ease;
}
.btn svg { display: block; }
.btn:hover { background: var(--ik-bg2); color: var(--ik-text); }
.btn[data-tooltip]::before {
  content: attr(data-tooltip);
  position: absolute;
  right: calc(100% + 10px);
  top: 50%;
  transform: translateY(-50%);
  white-space: nowrap;
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 6px;
  background: var(--ik-text);
  color: var(--ik-bg);
  pointer-events: none;
  opacity: 0;
  transition: opacity .1s ease;
  box-shadow: 0 2px 8px rgba(0,0,0,.15);
}
.btn[data-tooltip]::after {
  content: '';
  position: absolute;
  right: calc(100% + 5px);
  top: 50%;
  transform: translateY(-50%);
  border: 4px solid transparent;
  border-left-color: var(--ik-text);
  pointer-events: none;
  opacity: 0;
  transition: opacity .1s ease;
}
.btn[data-tooltip]:hover::before,
.btn[data-tooltip]:hover::after { opacity: 1; }
/* Clear-page tooltip points up — hide the side arrow */
.clear-wrap > .btn:first-child[data-tooltip]::after { display: none; }
.btn.active { background: var(--ik-accent); color: #fff; }
.btn.active:hover { background: var(--ik-accent-h); }

.divider { width: calc(100% - 16px); height: 1px; background: var(--ik-border); margin: 3px 0; opacity: 0.6; }

.badge {
  position: absolute;
  top: -3px; right: -3px;
  min-width: 16px; height: 16px;
  background: #ef4444;
  color: #fff;
  border-radius: 8px;
  font-size: 10px; font-weight: 600;
  display: flex; align-items: center; justify-content: center;
  padding: 0 4px;
  line-height: 1;
}

.danger-btn { color: var(--ik-muted); opacity: .6; }
.danger-btn:hover { opacity: 1; color: #ef4444; }

.clear-wrap {
  position: relative;
  display: flex;
  align-items: center;
}
.clear-all-btn {
  display: none;
  position: absolute;
  right: 100%;
  top: 0;
  background: var(--ik-bg);
  box-shadow: var(--ik-shadow);
  border-radius: 8px;
}
/* clear-all tooltip inherits from .btn[data-tooltip]::before */
/* Invisible bridge so hover doesn't break crossing the gap */
.clear-all-btn::after {
  content: '';
  position: absolute;
  top: 0;
  left: 100%;
  width: 6px;
  height: 100%;
}
/* Clear-page tooltip shows above-left so it doesn't cover the clear-all button */
.clear-wrap > .btn:first-child[data-tooltip]::before {
  right: 0;
  left: auto;
  top: auto;
  bottom: calc(100% + 8px);
  transform: none;
}
.clear-wrap:hover .clear-all-btn { display: flex; align-items: center; justify-content: center; }

`

/** Popup shadow DOM styles — fully isolated */
export const POPUP_CSS = /* css */ `
:host {
  all: initial;
  display: block;
  position: fixed;
  z-index: 2147483647;
}

* { box-sizing: border-box; }

:host {
  --ik-accent: #5b5bd6;
  --ik-accent-h: #4747be;
  --ik-bg: #ffffff;
  --ik-bg2: #f7f7f8;
  --ik-border: #e2e2e5;
  --ik-text: #18181b;
  --ik-muted: #71717a;
  --ik-shadow: 0 4px 24px rgba(0,0,0,.12);
  --ik-radius: 10px;
  --ik-hl: rgba(91,91,214,.12);
  --ik-focus-ring: 0 0 0 3px color-mix(in srgb, #5b5bd6 18%, transparent);
}

.popup {
  width: 340px;
  max-width: min(340px, calc(100vw - 20px));
  max-height: calc(100vh - 20px);
  background: var(--ik-bg);
  border: 1px solid var(--ik-border);
  border-radius: var(--ik-radius);
  box-shadow: var(--ik-shadow);
  padding: 16px;
  overflow: auto;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 13px;
  line-height: 1.5;
  color: var(--ik-text);
  animation: pop-in .18s cubic-bezier(0.16, 1, 0.3, 1);
}
@keyframes pop-in {
  from { opacity:0; transform: scale(.97) translateY(6px); }
  to   { opacity:1; transform: scale(1) translateY(0); }
}

.header { display:flex; align-items:center; justify-content:space-between; margin-bottom:12px; }
.element-tag {
  font-size:11px; font-family:ui-monospace,monospace; color:var(--ik-muted);
  background:var(--ik-bg2); border-radius:6px; padding:3px 7px;
  max-width:220px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
}
.close-btn {
  background: none; border: none; color: var(--ik-muted);
  cursor: pointer; padding: 4px; border-radius: 6px;
  display: flex; align-items: center; justify-content: center;
  transition: color .1s, background .1s;
}
.close-btn:hover { color: var(--ik-text); background: var(--ik-bg2); }

.fw-badge {
  display:inline-flex; align-items:center; gap:4px;
  font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.05em;
  color:var(--ik-accent); background:var(--ik-hl); border-radius:4px;
  padding:2px 6px; margin-bottom:8px;
}
.selected-text {
  font-size:12px; color:var(--ik-muted); background:var(--ik-bg2);
  border-left:3px solid var(--ik-accent); padding:4px 8px;
  border-radius:0 4px 4px 0; margin-bottom:10px;
  overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
}

.issue-context {
  margin-bottom: 12px;
  padding: 12px;
  border: 1px solid var(--ik-border);
  border-radius: 10px;
  background: var(--ik-bg2);
}
.issue-context-label {
  display: block;
  margin-bottom: 4px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .05em;
  color: var(--ik-accent);
}
.issue-context-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--ik-text);
  line-height: 1.35;
}
.issue-context-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
}

.field-block { display: block; margin-bottom: 10px; }
.popup-grid { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
.popup-grid .field-block { margin-bottom: 10px; }

.label {
  font-size:11px; font-weight:600; color:var(--ik-muted); margin-bottom:4px;
}
.row { display:flex; gap:6px; margin-bottom:10px; }
.chips { display:flex; gap:4px; flex-wrap:wrap; }

.chip {
  font-size:11px; padding:3px 8px; border-radius:12px;
  border:1px solid var(--ik-border); background:transparent;
  color:var(--ik-muted); cursor:pointer; transition:all .1s;
}
.chip:hover { border-color:var(--ik-accent); color:var(--ik-accent); }
.chip.sel { background:var(--ik-accent); border-color:var(--ik-accent); color:#fff; }
.chip.blocking.sel  { background:#ef4444; border-color:#ef4444; }
.chip.important.sel { background:#f97316; border-color:#f97316; }
.chip.suggestion.sel{ background:#22c55e; border-color:#22c55e; }

.screenshot-slot { margin-bottom: 10px; }

.btn-capture {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 8px 10px;
  border: 1px dashed var(--ik-border);
  border-radius: 6px;
  background: var(--ik-bg2);
  color: var(--ik-muted);
  font-size: 12px;
  font-family: inherit;
  cursor: pointer;
  transition: border-color .15s, color .15s;
}
.btn-capture:hover {
  border-color: var(--ik-accent);
  color: var(--ik-accent);
}
.btn-capture svg { flex-shrink: 0; }

.screenshot-preview {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--ik-border);
  margin-bottom: 10px;
  background: var(--ik-bg2);
  min-height: 60px;
}
.screenshot-preview img {
  display: block;
  width: 100%;
  height: auto;
  max-height: 80px;
  object-fit: contain;
  margin: 0 auto;
  cursor: zoom-in;
}
.screenshot-expand-btn {
  display: block;
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  cursor: zoom-in;
}
.screenshot-expand-btn:focus-visible {
  outline: 2px solid var(--ik-accent);
  outline-offset: 2px;
}
.screenshot-expand-hint {
  position: absolute;
  bottom: 6px;
  right: 6px;
  background: rgba(0,0,0,.5);
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  padding: 3px 7px;
  border-radius: 4px;
  pointer-events: none;
  letter-spacing: 0.01em;
}
.screenshot-remove {
  position: absolute;
  top: 6px; right: 6px;
  width: 22px; height: 22px;
  border-radius: 50%;
  border: none;
  background: rgba(0,0,0,.55);
  color: #fff;
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background .1s;
}
.screenshot-remove:hover { background: #ef4444; }

.screenshot-lightbox {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0,0,0,.7);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  z-index: 2147483648;
  animation: lightbox-in .15s cubic-bezier(0.16, 1, 0.3, 1);
}
@keyframes lightbox-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}
.screenshot-lightbox-card {
  width: min(1000px, calc(100vw - 40px));
  max-height: calc(100vh - 40px);
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  overflow: hidden;
  background: var(--ik-bg);
  border: 1px solid var(--ik-border);
  box-shadow: 0 24px 64px rgba(0,0,0,.4);
}
.screenshot-lightbox-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid var(--ik-border);
  flex-shrink: 0;
}
.screenshot-lightbox-title {
  font-size: 11px;
  font-weight: 600;
  color: var(--ik-muted);
}
.screenshot-lightbox-body {
  overflow: auto;
  padding: 16px;
  background: var(--ik-bg2);
  display: flex;
  align-items: center;
  justify-content: center;
}
.screenshot-lightbox-body img {
  display: block;
  max-width: 100%;
  height: auto;
  object-fit: contain;
  border-radius: 4px;
}

textarea {
  width:100%; min-height:80px; resize:vertical;
  border:1px solid var(--ik-border); border-radius:8px;
  background:var(--ik-bg2); color:var(--ik-text);
  font-family:inherit; font-size:13px; line-height:1.45; padding:10px 12px;
  outline:none; transition:border-color .15s, box-shadow .15s; margin-bottom:10px;
}
textarea:focus { border-color:var(--ik-accent); box-shadow: var(--ik-focus-ring); }
textarea::placeholder { color:var(--ik-muted); }

.text-input {
  width: 100%;
  height: 38px;
  border: 1px solid var(--ik-border);
  border-radius: 8px;
  background: var(--ik-bg2);
  color: var(--ik-text);
  font-family: inherit;
  font-size: 13px;
  padding: 0 10px;
  outline: none;
  transition: border-color .15s, box-shadow .15s;
}
.text-input:focus { border-color: var(--ik-accent); box-shadow: var(--ik-focus-ring); }
.text-input::placeholder { color: var(--ik-muted); }

.text-btn {
  border: none;
  background: transparent;
  color: var(--ik-accent);
  font-family: inherit;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  padding: 0;
}
.text-btn:hover { color: var(--ik-accent-h); }

.field-grid { display:grid; grid-template-columns:1fr 1fr; gap:8px; margin-bottom:10px; }
.field-grid.single-field { grid-template-columns: 1fr; }
.field-label {
  display:block; font-size:11px; font-weight:600;
  color:var(--ik-muted); margin-bottom:4px;
}
select {
  width:100%; height:36px; border:1px solid var(--ik-border); border-radius:8px;
  background:var(--ik-bg2); color:var(--ik-text); font-family:inherit; font-size:12px;
  padding:0 8px; outline:none; transition:border-color .15s, box-shadow .15s;
}
select:focus { border-color:var(--ik-accent); box-shadow: var(--ik-focus-ring); }

.details { margin-bottom:10px; }
.details summary {
  color:var(--ik-muted); font-size:11px; font-weight:700; cursor:pointer;
  margin-bottom:8px; user-select:none;
}
.details summary:hover { color:var(--ik-accent); }
.detail-input { min-height:52px; margin-bottom:8px; }

.actions { display:flex; flex-wrap:wrap; justify-content:flex-end; gap:8px; }
.actions .btn-secondary, .actions .btn-primary { white-space:nowrap; }

.submit-status {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 10px;
  padding: 10px 12px;
  border: 1px solid var(--ik-border);
  border-radius: 8px;
  background: var(--ik-bg2);
  color: var(--ik-muted);
  font-size: 12px;
  line-height: 1.4;
}
.submit-status[hidden] { display: none; }
.submit-status.error {
  color: #b42318;
  border-color: color-mix(in srgb, #b42318 24%, var(--ik-border));
  background: color-mix(in srgb, #b42318 6%, var(--ik-bg2));
}
.submit-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid color-mix(in srgb, var(--ik-accent) 18%, var(--ik-border));
  border-top-color: var(--ik-accent);
  border-radius: 999px;
  animation: popup-spin .75s linear infinite;
  flex: 0 0 auto;
}
@keyframes popup-spin {
  to { transform: rotate(360deg); }
}

.btn-secondary {
  padding:8px 14px; border-radius:8px; border:1px solid var(--ik-border);
  background:transparent; color:var(--ik-muted); font-size:12px; cursor:pointer; transition:all .1s;
  font-family: inherit;
}
.btn-secondary:hover { border-color:var(--ik-muted); color:var(--ik-text); }
.btn-secondary:disabled { opacity:.5; cursor:not-allowed; }

.btn-primary {
  padding:8px 16px; border-radius:8px; border:none;
  background:var(--ik-accent); color:#fff;
  font-size:12px; font-weight:600; letter-spacing:-0.01em; cursor:pointer;
  font-family: inherit; transition:background .15s;
}
.btn-primary:hover { background:var(--ik-accent-h); }
.btn-primary:disabled { opacity:.5; cursor:not-allowed; }
.close-btn:disabled { opacity:.45; cursor:not-allowed; }

.btn-danger {
  padding:6px 14px; border-radius:6px; border:1px solid #ef4444;
  background:transparent; color:#ef4444;
  font-size:12px; cursor:pointer; transition:all .1s;
}
.btn-danger:hover { background:#ef4444; color:#fff; }

/* Thread view */
.thread { margin-top:10px; border-top:1px solid var(--ik-border); padding-top:10px; }
.msg { margin-bottom:8px; }
.msg-role {
  font-size:10px; font-weight:700; text-transform:uppercase;
  letter-spacing:.05em; margin-bottom:2px;
}
.msg-role.human { color:var(--ik-accent); }
.msg-role.agent { color:#22c55e; }
.msg-content { font-size:12px; line-height:1.5; }

.status-badge {
  display:inline-flex; align-items:center; gap:4px;
  font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.05em;
  border-radius:4px; padding:2px 6px;
}
.status-badge.pending      { background:rgba(99,102,241,.15); color:var(--ik-accent); }
.status-badge.resolved     { background:rgba(34,197,94,.15); color:#22c55e; }
.status-badge.dismissed    { background:var(--ik-bg2); color:var(--ik-muted); }
`

/** Issue drawer shadow DOM styles — shares the same visual language as popup/toolbar */
export const DRAWER_CSS = /* css */ `
:host {
  all: initial;
  display: block;
  position: fixed;
  z-index: 2147483646;
}

:host([data-pip]) {
  position: static;
  z-index: auto;
  width: 100%;
  height: 100%;
}

* { box-sizing: border-box; }

:host {
  --ik-accent: #5b5bd6;
  --ik-accent-h: #4747be;
  --ik-bg: #ffffff;
  --ik-bg2: #f7f7f8;
  --ik-border: #e2e2e5;
  --ik-text: #18181b;
  --ik-muted: #71717a;
  --ik-shadow: 0 8px 32px rgba(0,0,0,.12), 0 0 0 1px rgba(0,0,0,.04);
  --ik-radius: 10px;
  --ik-focus-ring: 0 0 0 3px color-mix(in srgb, #5b5bd6 18%, transparent);
}

:host([data-pip]) .drawer {
  width: 100%;
  max-height: 100%;
  height: 100%;
  border: none;
  border-radius: 0;
  box-shadow: none;
}
:host([data-pip]) .drawer-list,
:host([data-pip]) .drawer-table {
  max-height: 100%;
}
:host([data-pip]) .header {
  cursor: default;
}

.drawer {
  width: min(392px, calc(100vw - 24px));
  max-height: calc(100vh - 32px);
  display: flex;
  flex-direction: column;
  background: var(--ik-bg);
  border: 1px solid var(--ik-border);
  border-radius: var(--ik-radius);
  box-shadow: var(--ik-shadow);
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  line-height: 1.5;
  color: var(--ik-text);
  overflow: hidden;
  animation: drawer-in .2s cubic-bezier(0.16, 1, 0.3, 1);
}
.drawer-list {
  max-height: min(680px, calc(100vh - 64px));
}
@keyframes drawer-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }

.header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding: 16px 16px 12px;
  border-bottom: 1px solid var(--ik-border);
  cursor: move;
}
.header button { cursor: pointer; }
.drag-dots {
  display: flex;
  gap: 4px;
  margin-bottom: 10px;
}
.drag-dots span {
  width: 4px;
  height: 4px;
  border-radius: 999px;
  background: var(--ik-border);
}
.title { margin-top: 4px; font-size: 22px; font-weight: 700; line-height: 1.05; letter-spacing: -.02em; }
.subtitle { margin-top: 6px; font-size: 12px; color: var(--ik-muted); line-height: 1.4; }
.close-btn {
  border: none;
  background: transparent;
  color: var(--ik-muted);
  cursor: pointer;
  padding: 5px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color .1s, background .1s;
}
.close-btn:hover { color: var(--ik-text); background: var(--ik-bg2); }

.actions {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 16px 12px;
  border-bottom: 1px solid var(--ik-border);
}
.btn-primary, .btn-secondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all .1s;
}
.btn-primary {
  border: 1px solid var(--ik-border);
  background: var(--ik-bg);
  color: var(--ik-text);
  padding: 8px 12px;
}
.btn-primary:hover { border-color: var(--ik-text); }
.btn-secondary {
  border: 1px solid var(--ik-border);
  background: var(--ik-bg2);
  color: var(--ik-muted);
  padding: 8px 12px;
}
.btn-secondary:hover { border-color: var(--ik-muted); color: var(--ik-text); }

.btn-spinner {
  width: 14px;
  height: 14px;
  border: 2.5px solid var(--ik-accent);
  border-top-color: transparent;
  border-radius: 999px;
  animation: drawer-spin .75s linear infinite;
  flex-shrink: 0;
  margin-left: 0;
  opacity: 0;
  visibility: hidden;
  transition: opacity .15s ease, margin-left .15s ease, visibility .15s;
}
.btn-loading .btn-spinner {
  margin-left: 6px;
  opacity: 1;
  visibility: visible;
}
.btn-loading .btn-label {
  opacity: 0.55;
}

.list {
  padding: 12px 16px 16px;
  overflow: auto;
}
.drawer-list .header {
  padding: 14px 16px 10px;
}
.drawer-list .drag-dots {
  margin-bottom: 8px;
}
.drawer-list .title {
  font-size: 20px;
}
.drawer-list .subtitle {
  margin-top: 4px;
}
.drawer-list .actions {
  padding: 10px 16px;
}
.drawer-list .list {
  padding: 10px 12px 12px;
}
.list-filter {
  margin-bottom: 12px;
}
.list-filter-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 8px;
}
.list-filter-grid--table {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}
.list-filter .field-block {
  margin-bottom: 0;
}
.field-span-full {
  grid-column: 1 / -1;
}
.drawer-table .header {
  padding: 14px 16px 10px;
}
.drawer-table .drag-dots {
  display: none;
}
.drawer-table .title {
  margin-top: 0;
  font-size: 20px;
}
.drawer-table .subtitle {
  margin-top: 4px;
}
.drawer-table .actions {
  padding: 10px 16px;
}
.drawer-table .list {
  padding: 10px 12px 12px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
}
.items-table-wrap {
  flex: 1;
  min-height: 0;
  overflow: auto;
  border: 1px solid var(--ik-border);
  border-radius: 10px;
  background: var(--ik-bg);
}
.items-table {
  width: 100%;
  min-width: 860px;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 12px;
}
.items-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 8px 10px;
  background: var(--ik-bg2);
  border-bottom: 1px solid var(--ik-border);
  color: var(--ik-muted);
  font-size: 11px;
  font-weight: 650;
  text-align: left;
  white-space: nowrap;
}
.items-table tbody td {
  padding: 6px 8px;
  border-bottom: 1px solid var(--ik-border);
  vertical-align: middle;
  background: var(--ik-bg);
}
.items-table-row:hover td {
  background: color-mix(in srgb, var(--ik-accent) 4%, var(--ik-bg));
}
.items-table-index {
  width: 36px;
  color: var(--ik-muted);
  font-variant-numeric: tabular-nums;
  text-align: center;
}
.items-table-title {
  min-width: 180px;
}
.items-table-page {
  max-width: 140px;
  color: var(--ik-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.items-table-updated {
  white-space: nowrap;
  color: var(--ik-muted);
  font-size: 11px;
}
.items-table-actions {
  white-space: nowrap;
  width: 1%;
}
.items-table-group td {
  padding: 0;
  background: var(--ik-bg2);
  border-bottom: 1px solid var(--ik-border);
}
.items-table-group-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  font-size: 12px;
  font-weight: 650;
  color: var(--ik-text);
}
.items-table-group-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--ik-accent) 12%, var(--ik-bg));
  color: var(--ik-accent);
  font-size: 11px;
  font-weight: 700;
}
.table-input,
.table-select {
  width: 100%;
  min-width: 0;
  padding: 5px 7px;
  border: 1px solid var(--ik-border);
  border-radius: 6px;
  background: var(--ik-bg);
  color: var(--ik-text);
  font: inherit;
  font-size: 12px;
  line-height: 1.3;
}
.table-input:focus,
.table-select:focus {
  outline: none;
  border-color: var(--ik-accent);
  box-shadow: var(--ik-focus-ring);
}
.table-input.table-cell-saving,
.table-select.table-cell-saving {
  opacity: 0.65;
}
.table-select {
  cursor: pointer;
}
.table-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 28px;
  padding: 0 8px;
  border: 1px solid var(--ik-border);
  border-radius: 6px;
  background: var(--ik-bg);
  color: var(--ik-text);
  font: inherit;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
}
.table-action-btn + .table-action-btn {
  margin-left: 4px;
}
.table-action-btn:hover {
  border-color: var(--ik-accent);
  background: var(--ik-bg2);
}
.table-action-btn--danger {
  width: 28px;
  padding: 0;
  color: var(--ik-muted);
}
.table-action-btn--danger:hover {
  color: #dc2626;
  border-color: color-mix(in srgb, #dc2626 35%, var(--ik-border));
}
.table-action-btn:disabled {
  opacity: 0.6;
  cursor: default;
}
.empty {
  padding: 36px 16px;
  text-align: center;
  color: var(--ik-muted);
  font-size: 13px;
  line-height: 1.5;
}
.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px;
  margin-bottom: 12px;
  border: 1px solid var(--ik-border);
  border-radius: 10px;
  background: var(--ik-bg2);
  color: var(--ik-muted);
  font-size: 13px;
  line-height: 1.4;
}
.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid color-mix(in srgb, var(--ik-accent) 18%, var(--ik-border));
  border-top-color: var(--ik-accent);
  border-radius: 999px;
  animation: drawer-spin .75s linear infinite;
  flex: 0 0 auto;
}
.loading-copy {
  min-width: 0;
}
@keyframes drawer-spin {
  to { transform: rotate(360deg); }
}
.empty-icon {
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
  opacity: 0.35;
}
.issue-row {
  width: 100%;
  display: block;
  padding: 14px 15px;
  margin-bottom: 10px;
  border: 1px solid var(--ik-border);
  border-radius: 10px;
  background: var(--ik-bg);
  color: var(--ik-text);
  text-align: left;
  font-family: inherit;
  cursor: pointer;
  transition: border-color .12s, background .12s;
}
.drawer-list .issue-row {
  padding: 10px 12px;
  margin-bottom: 8px;
  border-radius: 10px;
}
.issue-row { position: relative; }
.issue-row:hover { border-color: var(--ik-accent); background: var(--ik-bg2); }
.issue-row:focus-visible { outline: none; border-color: var(--ik-accent); box-shadow: var(--ik-focus-ring); }
.issue-row-close {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  color: var(--ik-muted);
  cursor: pointer;
  opacity: 0;
  transition: opacity .12s, color .12s, border-color .12s, background .12s;
}
.issue-row:hover .issue-row-close,
.issue-row:focus-within .issue-row-close { opacity: 1; }
.issue-row-close:hover { color: #dc2626; border-color: var(--ik-border); background: var(--ik-bg); }
.issue-row-close:disabled { opacity: 1; cursor: default; pointer-events: none; }
.issue-row-close-spinner {
  display: block;
  width: 12px;
  height: 12px;
  border: 1.5px solid var(--ik-border);
  border-top-color: var(--ik-muted);
  border-radius: 999px;
  animation: drawer-spin .75s linear infinite;
}
.list-search { margin-bottom: 10px; }
.issue-row-main { display:flex; align-items:flex-start; gap:10px; min-width:0; }
.drawer-list .issue-row-main { gap: 8px; }
.issue-index,
.pin {
  width: 20px; height: 20px; flex: 0 0 20px;
  display:flex; align-items:center; justify-content:center;
  border-radius:50%; background:var(--ik-accent); color:#fff;
  font-size:10px; font-weight:800;
}
.issue-index {
  width: 26px;
  height: 26px;
  flex-basis: 26px;
}
.drawer-list .issue-index {
  width: 24px;
  height: 24px;
  flex-basis: 24px;
}
.issue-copy { min-width: 0; flex: 1; }
.issue-title {
  font-size: 14px;
  font-weight: 650;
  line-height: 1.35;
  color: var(--ik-text);
}
.drawer-list .issue-title {
  font-size: 13px;
  line-height: 1.35;
}
.issue-path {
  margin-top: 2px;
  font-size: 11px;
  color: var(--ik-muted);
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.drawer-list .issue-path {
  font-size: 10px;
  line-height: 1.25;
}
.issue-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
}
.drawer-list .issue-badges {
  margin-top: 5px;
}
.issue-badge {
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 650;
  line-height: 1.5;
  letter-spacing: .01em;
  text-transform: capitalize;
}
.drawer-list .issue-badge {
  font-size: 9.5px;
  padding: 1px 5px;
}
.issue-badge--open { background: color-mix(in srgb, #5b5bd6 14%, var(--ik-bg)); color: #5b5bd6; }
.issue-badge--in_progress { background: color-mix(in srgb, #b45309 14%, var(--ik-bg)); color: #b45309; }
.issue-badge--reopened { background: color-mix(in srgb, #7c3aed 14%, var(--ik-bg)); color: #7c3aed; }
.issue-badge--resolved { background: color-mix(in srgb, #15803d 14%, var(--ik-bg)); color: #15803d; }
.issue-badge--closed { background: color-mix(in srgb, #78716c 14%, var(--ik-bg)); color: #78716c; }
.issue-badge--low { background: color-mix(in srgb, #71717a 12%, var(--ik-bg)); color: #71717a; }
.issue-badge--medium { background: color-mix(in srgb, #5b5bd6 14%, var(--ik-bg)); color: #5b5bd6; }
.issue-badge--high { background: color-mix(in srgb, #b45309 14%, var(--ik-bg)); color: #b45309; }
.issue-badge--critical { background: color-mix(in srgb, #dc2626 14%, var(--ik-bg)); color: #dc2626; }
.issue-badge--minor { background: color-mix(in srgb, #71717a 12%, var(--ik-bg)); color: #71717a; }
.issue-badge--major { background: color-mix(in srgb, #b45309 14%, var(--ik-bg)); color: #b45309; }
.issue-badge--blocker { background: color-mix(in srgb, #dc2626 14%, var(--ik-bg)); color: #dc2626; }
.issue-meta {
  margin-top: 4px;
  font-size: 11px;
  color: var(--ik-muted);
  line-height: 1.35;
}
.drawer-list .issue-meta {
  margin-top: 3px;
  font-size: 10px;
  line-height: 1.25;
}
.issue-stats {
  font-size: 11px;
  color: var(--ik-muted);
  line-height: 1.3;
}
.issue-severity {
  margin-top: 6px;
  font-size: 11px;
  font-weight: 700;
  text-transform: capitalize;
}
.issue-severity.blocking { color: #dc2626; }
.issue-severity.important { color: #b45309; }
.issue-severity.suggestion { color: #15803d; }
.issue-status {
  font-size: 11px;
  font-weight: 700;
  text-transform: capitalize;
  color: var(--ik-muted);
}
.issue-status.open { color: var(--ik-accent); }
.issue-status.in_progress { color: #b45309; }
.issue-status.reopened { color: #7c3aed; }
.issue-status.resolved { color: #15803d; }
.issue-status.closed { color: #78716c; }

.issue-detail {
  position: relative;
  border-top: 1px solid var(--ik-border);
  padding-top: 8px;
}
.issue-detail-body {
  min-height: 320px;
}
.issue-detail.loading .issue-detail-body {
  opacity: 0.35;
}
.detail-alert {
  margin-bottom: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.45;
}
.detail-alert-error {
  background: color-mix(in srgb, #dc2626 10%, var(--ik-bg));
  border: 1px solid color-mix(in srgb, #dc2626 22%, var(--ik-border));
  color: #b91c1c;
}
.detail-loading-overlay {
  position: absolute;
  inset: 6px 0 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 48px;
  background: color-mix(in srgb, var(--ik-bg) 88%, transparent);
  pointer-events: all;
}
.detail-loading-overlay .loading-state {
  margin-bottom: 0;
  background: var(--ik-bg);
}
.field-block { display: block; margin-bottom: 10px; }
.field-label {
  display: block;
  margin-bottom: 4px;
  font-size: 11px;
  font-weight: 600;
  color: var(--ik-muted);
}
.text-input,
.detail-input,
select {
  width: 100%;
  border: 1px solid var(--ik-border);
  border-radius: 8px;
  background: var(--ik-bg2);
  color: var(--ik-text);
  font-family: inherit;
  font-size: 12px;
  outline: none;
  transition: border-color .15s, box-shadow .15s;
}
.text-input,
select {
  height: 38px;
  padding: 0 10px;
}
.detail-input {
  min-height: 72px;
  padding: 10px 12px;
  resize: vertical;
  line-height: 1.45;
}
.text-input:focus,
.detail-input:focus,
select:focus {
  border-color: var(--ik-accent);
  box-shadow: var(--ik-focus-ring);
}

.field-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 12px;
}
.detail-hero {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 14px;
}
.detail-kicker {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
  color: var(--ik-muted);
}
.detail-route {
  font-size: 12px;
  color: var(--ik-muted);
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.detail-badges,
.detail-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}
.detail-support {
  font-size: 11px;
  line-height: 1.4;
  color: var(--ik-muted);
}
.issue-summary {
  margin-bottom: 12px;
  font-size: 12px;
  line-height: 1.5;
  color: var(--ik-muted);
}
.detail-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 14px;
}
.issue-detail-body > .screenshot-preview,
.issue-detail-body > .detail-actions,
.issue-detail-body > .annotation-detail-list,
.issue-detail-body > .empty {
  margin-top: 12px;
}
.detail-button-row {
  display: flex;
  justify-content: flex-start;
  margin: 0;
}
.detail-button-row .btn-primary,
.detail-button-row .btn-secondary {
  min-width: 108px;
}
.text-btn {
  border: none;
  background: transparent;
  color: var(--ik-accent);
  font: inherit;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  padding: 0;
}
.status-actions {
  display: flex;
  gap: 8px;
}
.status-btn {
  flex: 1;
  border: 1px solid var(--ik-border);
  border-radius: 8px;
  background: var(--ik-bg2);
  color: var(--ik-muted);
  font: inherit;
  font-size: 11px;
  font-weight: 600;
  padding: 9px 10px;
  cursor: pointer;
}
.status-btn.current {
  border-color: var(--ik-text);
  color: var(--ik-text);
  background: var(--ik-bg);
}
.status-btn:hover {
  border-color: var(--ik-text);
  color: var(--ik-text);
}
.annotation-detail-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.screenshot-preview {
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  border: 1px solid var(--ik-border);
  background: var(--ik-bg2);
  min-height: 140px;
}
.screenshot-preview-loading {
  overflow: hidden;
}
.screenshot-preview-loading::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent 0%, rgba(91, 91, 214, 0.08) 50%, transparent 100%);
  animation: drawer-loading-sheen 1.1s ease-in-out infinite;
}
@keyframes drawer-loading-sheen {
  from { transform: translateX(-100%); }
  to { transform: translateX(100%); }
}
.screenshot-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--ik-border);
}
.screenshot-controls {
  display: flex;
  align-items: center;
  gap: 6px;
}
.screenshot-zoom-label {
  min-width: 44px;
  font-size: 11px;
  font-weight: 600;
  color: var(--ik-muted);
  text-align: right;
}
.zoom-btn {
  border: 1px solid var(--ik-border);
  border-radius: 8px;
  background: var(--ik-bg);
  color: var(--ik-text);
  font: inherit;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  padding: 6px 9px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition: border-color .1s, color .1s;
}
.zoom-btn:hover:not(:disabled) {
  border-color: var(--ik-text);
}
.zoom-btn:focus-visible {
  outline: none;
  box-shadow: var(--ik-focus-ring);
}
.zoom-btn:disabled {
  cursor: default;
  opacity: .45;
}
.screenshot-viewport {
  overflow: auto;
  padding: 12px;
  max-height: 320px;
  scroll-behavior: smooth;
  cursor: zoom-in;
}
.screenshot-viewport.zoomed { cursor: move; }
.screenshot-viewport img {
  transition: transform .2s cubic-bezier(0.16, 1, 0.3, 1);
  transform-origin: top center;
}
.screenshot-modal {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0,0,0,.6);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  z-index: 2147483647;
}
.screenshot-modal-card {
  width: min(960px, calc(100vw - 40px));
  max-height: calc(100vh - 40px);
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  overflow: hidden;
  background: var(--ik-bg);
  border: 1px solid var(--ik-border);
  box-shadow: var(--ik-shadow);
}
.screenshot-modal-toolbar {
  flex-shrink: 0;
}
.screenshot-modal-viewport {
  overflow: auto;
  padding: 16px;
  max-height: calc(100vh - 120px);
  background: var(--ik-bg2);
  scroll-behavior: smooth;
}
.screenshot-modal-viewport img {
  display: block;
  width: 100%;
  height: auto;
  object-fit: contain;
  margin: 0 auto;
  transition: transform .2s cubic-bezier(0.16, 1, 0.3, 1);
  transform-origin: top center;
}
.screenshot-preview img {
  display: block;
  width: 100%;
  height: auto;
  object-fit: contain;
  margin: 0 auto;
}
.annotation-row {
  width: 100%;
  display: block;
  text-align: left;
  border: 1px solid var(--ik-border);
  background: var(--ik-bg);
  color: var(--ik-text);
  border-radius: 10px;
  padding: 12px 13px;
  cursor: pointer;
  font-family: inherit;
  transition: border-color .12s, background .12s;
}
.annotation-row:hover {
  border-color: var(--ik-accent);
  background: var(--ik-bg2);
}
.annotation-row.editing {
  cursor: default;
}
.annotation-row.editing:hover {
  border-color: var(--ik-border);
  background: var(--ik-bg);
}
.edit-comment-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}
.annotation-row-top { display:flex; align-items:center; gap:8px; margin-bottom:8px; }
.comment-avatar {
  width: 24px;
  height: 24px;
  flex: 0 0 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: var(--ik-text);
  color: var(--ik-bg);
  font-size: 10px;
  font-weight: 700;
}
.comment {
  min-width: 0;
  flex: 1;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.45;
  word-break: break-word;
}
.annotation-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-left: 32px;
  font-size: 11px;
  color: var(--ik-muted);
  line-height: 1.4;
}
`

/** Marker pin styles injected into document.head — pins overlay the page */
export const MARKER_CSS = /* css */ `
.ik-marker {
  position: absolute;
  z-index: 2147483645;
  width: 26px; height: 26px;
  border-radius: 50%;
  background: var(--ik-marker-default, #5b5bd6);
  color: #fff;
  font-size: 11px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,.22);
  transition: transform .15s ease;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  pointer-events: all;
  user-select: none;
  overflow: visible;
}
.ik-marker:hover { transform: scale(1.12); }
.ik-marker.active {
  transform: scale(1.08);
  box-shadow: 0 0 0 3px rgba(255,255,255,.92), 0 0 0 6px rgba(91,91,214,.30), 0 6px 18px rgba(0,0,0,.28);
}
.ik-marker::after {
  content: '';
  position: absolute;
  bottom: -7px;
  left: 50%;
  transform: translateX(-50%);
  width: 0; height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 8px solid var(--ik-marker-default, #5b5bd6);
  pointer-events: none;
}
.ik-marker.dismissed { background: var(--ik-marker-dismissed, #71717a); }
.ik-marker.dismissed::after { border-top-color: var(--ik-marker-dismissed, #71717a); }
.ik-marker[data-anchor-state="fallback"] {
  opacity: .78;
}
.ik-marker.offscreen {
  opacity: .5;
  box-shadow: 0 0 0 2px rgba(255,255,255,.85), 0 2px 8px rgba(0,0,0,.22);
}
.ik-marker.offscreen::after { display: none; }
`

/** Inject styles into document.head (idempotent) */
export function injectGlobalStyles(colors?: import('../types').MarkerColors): void {
  injectGlobalStylesInto(document, colors)
}

/** Inject styles into a target document (used for Document Picture-in-Picture window) */
export function injectGlobalStylesInto(targetDoc: Document, colors?: import('../types').MarkerColors): void {
  if (targetDoc.getElementById('visual-qa-global')) return
  const vars = colors
    ? `:root {${colors.default ? ` --ik-marker-default: ${colors.default};` : ''}${colors.dismissed ? ` --ik-marker-dismissed: ${colors.dismissed};` : ''} }\n`
    : ''
  const style = targetDoc.createElement('style')
  style.id = 'visual-qa-global'
  style.textContent = vars + GLOBAL_CSS + MARKER_CSS
  targetDoc.head.appendChild(style)
}
